import React, { memo, useMemo } from "react";
import PropTypes from "prop-types";
import {
  generateProductPrices,
  parseCssStringToObject,
  pickRandomValueFromArray,
  resolveInlineFontFamily,
} from "../../../../helper";
import {
  DEFAULT_BLOCK_CSS,
  POPUP_SECTIONS,
  offerTypeUsesRuntimeResolution,
} from "../../../../constant/constant";
import { useApp } from "../../../../context/AppState";
import { usePopupFormContext } from "../../../../hooks/usePopupFormContext";
import {
  applyCatalogUpsellTextAlignStyles,
  applyTextAlignToFlexStyles,
  resolveSectionContentHeight,
} from "../../../../../shared/text-block-layout.js";
import { isSectionVisibleForViewport } from "../../../../../shared/section-visibility.js";
import useProductLink from "../../../../hooks/useProductLink";


const TextBlock = ({
  popupData,
  activeStep,
  index,
  section,
  parentData,
  catalogFlow = false,
  catalogLineClamp,
  /** "cart-drawer" | "product-page" — cart-drawer upsells keep full-title hit area */
  surface = "cart-drawer",
}) => {
  const { isMobile, appState } = useApp();
  const { watch } = usePopupFormContext();
  const { isClickable, navigate } = useProductLink({
    popupData,
    productData: parentData,
    clickTarget: "title",
  });
  // Title → product page for all product titles when link-to-product is on.
  const isProductTitleLink =
    isClickable &&
    section?.type === POPUP_SECTIONS.TITLE &&
    Boolean(parentData);
  // Only cart-drawer bottom upsells keep a full-width title click target.
  // Product page Add-Ons + other titles use text-only hit boxes (no empty-space redirect).
  const useFullWidthTitleHit =
    catalogFlow && surface === "cart-drawer";
  const viewKey = isMobile ? "mobile" : "desktop";

  if (!isSectionVisibleForViewport(section, isMobile)) {
    return null;
  }
  const customViewCss = section?.setting?.customCSS?.[viewKey] || {};

  const baseStyles = {
    ...DEFAULT_BLOCK_CSS,
    ...customViewCss,
    ...parseCssStringToObject(section?.setting?.overrideCss?.[viewKey]),
    flexWrap: "wrap",
    whiteSpace: "pre-wrap",
    rotate: customViewCss?.rotation ?? `0deg`,
  };

  const blockCSS = catalogFlow
    ? applyCatalogUpsellTextAlignStyles(baseStyles)
    : applyTextAlignToFlexStyles(baseStyles);

  const resolvedFontFamily = resolveInlineFontFamily(blockCSS.fontFamily);
  if (resolvedFontFamily) {
    blockCSS.fontFamily = resolvedFontFamily;
  }

  const resolvedHeight = resolveSectionContentHeight(section, viewKey);
  if (resolvedHeight && !blockCSS.height) {
    blockCSS.height = resolvedHeight;
  }

  if (section?.type === POPUP_SECTIONS.DESCRIPTION && blockCSS.height) {
    blockCSS.overflowY = blockCSS.overflowY || "auto";
    blockCSS.overflowX = blockCSS.overflowX || "hidden";
  }

  if (catalogLineClamp && section?.type === POPUP_SECTIONS.TITLE) {
    const lineHeight = 1.25;
    const fontSize = parseFloat(blockCSS.fontSize) || 11;
    const clampHeight = Math.ceil(fontSize * lineHeight * catalogLineClamp);
    blockCSS.display = "-webkit-box";
    blockCSS.WebkitLineClamp = catalogLineClamp;
    blockCSS.WebkitBoxOrient = "vertical";
    blockCSS.overflow = "hidden";
    blockCSS.textOverflow = "ellipsis";
    blockCSS.whiteSpace = "normal";
    blockCSS.wordBreak = "break-word";
    // Cap tall titles only — do not reserve empty height for short 1-line titles.
    blockCSS.maxHeight = `${clampHeight}px`;
    delete blockCSS.height;
    blockCSS.flexWrap = "nowrap";
    blockCSS.flex = "none";
  }

  const productsArray = popupData?.schema[activeStep]?.sections
    ?.filter((section) => section?.type === "PRODUCT")
    ?.flatMap((section) => section?.products || []);

  const selectedVariantIds = productsArray?.map((_, index) =>
    watch(`${POPUP_SECTIONS.VARIANT_PICKER}-${index}`)
  );

 
  const contentToDisplay = useMemo(() => {
    const productPriceData = generateProductPrices({
      products: productsArray,
      selectedVariantIds: selectedVariantIds,
      currency: window?.Shopify?.currency?.active,
      isPreviewMode: appState?.is_preview_mode,
      shopCurrency: appState?.shop?.currency,
    });
  
    const mergeData = {
      ...parentData,
      currency: productPriceData?.currency,
      discount_price: productPriceData?.totals?.discounted_price,
      total_price: productPriceData?.totals?.actual_price,
      description: parentData?.description || parentData?.body_html,
    };
  
    const replacePlaceholders = (text, data) => {
      return text.replace(/{{\s*([^{}\s]+)\s*}}/g, (_, key) => {
        const keys = key.split(".");
        let value = data;
        for (const k of keys) {
          value = value?.[isNaN(k) ? k : parseInt(k, 10)];
          if (value === undefined) break;
        }
        return value || "";
      });
    };
    if (parentData?.isDummy && section?.type === POPUP_SECTIONS.TITLE) {
      return parentData.title || "Product Title";
    }

    const productsToOfferType =
      popupData?.generalSettings?.condition?.product?.products_to_offer?.type;
    const sectionText = String(section?.text || "").trim();
    const catalogTitle = parentData?.title
      ? String(parentData.title).trim()
      : "";
    const usesTitlePlaceholder = /\{\{\s*title\s*\}\}/i.test(sectionText);
    const usesRuntimeCatalog =
      offerTypeUsesRuntimeResolution(productsToOfferType);
    const titleDiffersFromCatalog =
      Boolean(catalogTitle) && sectionText !== catalogTitle;

    if (
      section?.type === POPUP_SECTIONS.TITLE &&
      appState?.is_preview_mode &&
      usesRuntimeCatalog
    ) {
      if (parentData?.isDummy) {
        return parentData.title || "Product Title";
      }
      if (usesTitlePlaceholder) {
        return replacePlaceholders(sectionText, mergeData);
      }
      return "Product Title";
    }

    if (
      section?.type === POPUP_SECTIONS.TITLE &&
      catalogTitle &&
      !parentData?.isDummy &&
      (appState?.is_preview_mode ||
        usesRuntimeCatalog ||
        usesTitlePlaceholder ||
        titleDiffersFromCatalog)
    ) {
      const titleSource = usesTitlePlaceholder ? sectionText : catalogTitle;
      return replacePlaceholders(titleSource, mergeData);
    }

    if (
      section?.type === POPUP_SECTIONS.DESCRIPTION &&
      !appState?.is_preview_mode &&
      (usesRuntimeCatalog || /\{\{\s*description\s*\}\}/i.test(sectionText))
    ) {
      const descriptionSource =
        parentData?.description || parentData?.body_html || sectionText;
      return replacePlaceholders(
        /\{\{\s*description\s*\}\}/i.test(sectionText)
          ? sectionText
          : String(descriptionSource || ""),
        mergeData
      );
    }

    const textToDisplay =
    section?.AIText &&
    section?.setting?.isAiEnabled &&
    Array.isArray(section?.AIText) &&
    section?.AIText?.length > 0
    ? pickRandomValueFromArray(section?.AIText)
    : section?.text;
    
    return replacePlaceholders(textToDisplay || "", mergeData);
  }, [section, parentData, productsArray, selectedVariantIds, appState, popupData]);

  const titleLinkStyle = {
    cursor: "pointer",
    textDecoration: "none",
    color: "inherit",
  };

  // Cart drawer "YOU MIGHT ALSO LIKE": keep previous full-title click + line-clamp.
  if (isProductTitleLink && useFullWidthTitleHit) {
    return (
      <p
        style={{
          ...blockCSS,
          cursor: "pointer",
          textDecoration: "none",
          color: blockCSS.color || "inherit",
        }}
        role="link"
        tabIndex={0}
        onClick={navigate}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            navigate(event);
          }
        }}
        dangerouslySetInnerHTML={{ __html: contentToDisplay }}
      />
    );
  }

  // Product page Add-Ons / other titles: clickable text only (empty space does not navigate).
  if (isProductTitleLink) {
    return (
      <p style={blockCSS}>
        <span
          className="weupsell-product-title-link"
          style={titleLinkStyle}
          role="link"
          tabIndex={0}
          onClick={navigate}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              navigate(event);
            }
          }}
          dangerouslySetInnerHTML={{ __html: contentToDisplay }}
        />
      </p>
    );
  }

  return (
    <p
      style={blockCSS}
      dangerouslySetInnerHTML={{ __html: contentToDisplay }}
    />
  );
};

TextBlock.propTypes = {
  section: PropTypes.object,
  catalogFlow: PropTypes.bool,
  catalogLineClamp: PropTypes.number,
  surface: PropTypes.oneOf(["cart-drawer", "product-page"]),
};

export default memo(TextBlock);
